import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultName?: string;
  existingNames: string[];
  onSave: (input: { name: string; description: string | null }) => Promise<void>;
}

export function SaveAsTemplateModal({ open, onOpenChange, defaultName = "", existingNames, onSave }: Props) {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const willOverwrite = trimmed.length > 0 && existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());

  const handleSave = async () => {
    if (!trimmed) {
      toast.error("Please enter a template name");
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: trimmed, description: description.trim() || null });
      toast.success(willOverwrite ? "Template updated" : "Template saved");
      onOpenChange(false);
      setName("");
      setDescription("");
    } catch (e: any) {
      toast.error(e?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save dispatch as template</DialogTitle>
          <DialogDescription>
            Reuse this dispatch's stops, pallets, weight, commodity and notes on future loads. Dates are not saved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Chicago to Dallas — 24 pallets"
              maxLength={120}
            />
            {willOverwrite && (
              <p className="text-xs text-amber-600">A template with this name exists. Saving will overwrite it.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-desc">Description (optional)</Label>
            <Textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes for when to use this template"
              rows={3}
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !trimmed} className="bg-brand-teal hover:bg-brand-teal/90">
            {saving ? "Saving…" : willOverwrite ? "Overwrite" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
